import React, { useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from "@mui/material";
import { Cancel } from "@mui/icons-material";
import { cancelOneOffSession } from "./api";
import type { OneOffSession } from "./types";
import DialogHeader from "./DialogHeader";

interface CancelOneOffSessionDialogProps {
  target: { session: OneOffSession } | null;
  onClose: () => void;
  onSuccess: () => Promise<void> | void;
  onError: (message: string) => void;
}

const CancelOneOffSessionDialog: React.FC<CancelOneOffSessionDialogProps> = ({
  target,
  onClose,
  onSuccess,
  onError,
}) => {
  const [cancelling, setCancelling] = useState(false);

  const handleCancel = async () => {
    if (!target) return;
    try {
      setCancelling(true);
      await cancelOneOffSession(target.session.id);
      await onSuccess();
      onClose();
    } catch (err) {
      console.error("Error cancelling one-off session:", err);
      onError("Failed to cancel session. Please try again.");
    } finally {
      setCancelling(false);
    }
  };

  return (
    <Dialog
      open={target != null}
      onClose={() => !cancelling && onClose()}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogHeader
        title="Cancel Session"
        icon={<Cancel color="warning" />}
        onClose={onClose}
        closeDisabled={cancelling}
      />
      <DialogContent dividers>
        <Typography variant="body1">
          Are you sure you want to cancel{" "}
          <strong>{target?.session.name}</strong>?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          The session will be removed from your club administration view. This
          cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={cancelling}>
          Keep Session
        </Button>
        <Button
          variant="contained"
          color="warning"
          startIcon={cancelling ? <CircularProgress size={18} /> : <Cancel />}
          onClick={handleCancel}
          disabled={cancelling}
        >
          {cancelling ? "Cancelling…" : "Cancel Session"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CancelOneOffSessionDialog;
